import React, { useState, useEffect } from "react";
import { milestonesApi } from "../../../services/api/milestonesApi";
import "./PostForms.css";

const MilestonePostForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    type: "",
    title: "",
    venue: "",
    description: "",
    date: "",
    content: "",
    visibility: "public",
  });
  const [milestoneTypes, setMilestoneTypes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    loadMilestoneTypes();
  }, []);

  const loadMilestoneTypes = async () => {
    // Use hardcoded default types (correct format)
    const defaultTypes = [
      { value: "paper_published", label: "Paper Published" },
      { value: "paper_accepted", label: "Paper Accepted" },
      { value: "grant_awarded", label: "Grant Awarded" },
      { value: "phd_defense", label: "PhD Defense" },
      { value: "conference_talk", label: "Conference Talk" },
      { value: "award_received", label: "Award Received" },
      { value: "patent_granted", label: "Patent Granted" },
      { value: "project_completed", label: "Project Completed" },
      { value: "other", label: "Other" },
    ];

    try {
      const response = await milestonesApi.getMilestoneTypes();
      const apiTypes = response.data.types || [];

      // Check if API returned correct format (objects with value/label)
      if (apiTypes.length > 0 && apiTypes[0].value && apiTypes[0].label) {
        setMilestoneTypes(apiTypes);
      } else {
        // API returned wrong format or empty, use defaults
        setMilestoneTypes(defaultTypes);
      }
    } catch (err) {
      // Use default types if API fails
      setMilestoneTypes(defaultTypes);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!formData.type) {
      setError("Please select a milestone type");
      return;
    }

    if (!formData.title.trim()) {
      setError("Please enter a milestone title");
      return;
    }

    setSubmitting(true);

    try {
      const milestoneData = {
        milestone: {
          type: formData.type, // Enum value (e.g., 'phd_defense', not 'PhD Defense')
          title: formData.title,
          venue: formData.venue || undefined,
          description: formData.description || undefined,
          date: formData.date || new Date().toISOString(),
        },
        content: formData.content,
        visibility: formData.visibility,
      };

      const response = await milestonesApi.createMilestone(milestoneData);

      const createdPost =
        response.data.post || response.data.data || response.data;

      onSuccess(createdPost);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create milestone");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="post-form milestone-form">
      {error && <div className="error-message">{error}</div>}

      {/* Milestone Type */}
      <div className="form-group">
        <label htmlFor="type">Milestone Type *</label>
        <select
          id="type"
          value={formData.type}
          onChange={(e) => setFormData({ ...formData, type: e.target.value })}
          required
        >
          <option value="">Select a type...</option>
          {milestoneTypes.map((type) => (
            <option key={type.value} value={type.value}>
              {type.label}
            </option>
          ))}
        </select>
      </div>

      {/* Title */}
      <div className="form-group">
        <label htmlFor="title">Title *</label>
        <input
          type="text"
          id="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., Published in Nature"
          maxLength={200}
          required
        />
      </div>

      {/* Venue */}
      <div className="form-group">
        <label htmlFor="venue">Venue/Institution (Optional)</label>
        <input
          type="text"
          id="venue"
          value={formData.venue}
          onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
          placeholder="e.g., Nature Journal, MIT"
          maxLength={150}
        />
      </div>

      {/* Description */}
      <div className="form-group">
        <label htmlFor="description">Description (Optional)</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          placeholder="Brief description of the milestone..."
          rows={3}
          maxLength={500}
        />
      </div>

      {/* Date */}
      <div className="form-group">
        <label htmlFor="date">Date</label>
        <input
          type="date"
          id="date"
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
          max={new Date().toISOString().split("T")[0]}
        />
        <small>Leave empty to use today's date</small>
      </div>

      {/* Additional Content */}
      <div className="form-group">
        <label htmlFor="content">Additional Thoughts (Optional)</label>
        <textarea
          id="content"
          value={formData.content}
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
          placeholder="Share your thoughts, journey, or learnings..."
          rows={4}
          maxLength={2000}
        />
      </div>

      {/* Visibility */}
      <div className="form-group">
        <label htmlFor="visibility">Visibility</label>
        <select
          id="visibility"
          value={formData.visibility}
          onChange={(e) =>
            setFormData({ ...formData, visibility: e.target.value })
          }
        >
          <option value="public">Public</option>
          <option value="connections">Connections Only</option>
          <option value="private">Private</option>
        </select>
      </div>

      {/* Actions */}
      <div className="form-actions">
        <button type="button" onClick={onCancel} className="cancel-btn">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="submit-btn">
          {submitting ? "Sharing..." : "Share Milestone"}
        </button>
      </div>
    </form>
  );
};

export default MilestonePostForm;
